<script setup lang="ts">
// Component to be used with <RadioGroup/>
export type RadioProps = {
  name: string
  label?: string /* Optional as it could be a slot-ed content */
  hint?: string
  error?: string
  testId?: string
}

const props = defineProps<RadioProps>()
const emit = defineEmits(['select'])

const model = defineModel()
</script>

<template>
  <div class="radio">
    <label class="radio__label" :data-testid="testId">
      <input
        type="radio"
        v-model="model"
        v-bind="$attrs"
        :name
        :aria-invalid="!!error"
        @change="$emit('select', model)"
      />
      <span>
        <slot>{{ label }}</slot>
      </span>
    </label>
    <div class="radio__content" v-if="$slots.content">
      <!-- Content within Radio, But not part of clickable area -->
      <slot name="content" />
    </div>
  </div>
</template>

<style lang="postcss">
.radio {
  .radio__label {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
  }

  input[type='radio'] {
    appearance: none;
    -webkit-appearance: none;
    box-sizing: border-box;
    background-color: var(--surface-light-00);
    margin-right: var(--space-30);
    height: 1rem;
    width: 1rem;
    min-height: 1rem;
    min-width: 1rem;
    border: 0.0625rem solid var(--border-neutral-25);
    border-radius: 50%;
    cursor: pointer;
    position: relative;
    transition:
      border-color 0.2s ease-out,
      box-shadow 0.2s ease-out;

    &:not(:checked):hover {
      outline: none;
      box-shadow: 0 0 0 var(--space-10) var(--border-neutral-25);
    }

    &:checked {
      background-color: var(--surface-dark-10);
      border-color: var(--surface-dark-10);
      border-width: var(--space-30);
      transition: all 0.3s ease-out;

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 0.5rem;
        height: 0.5rem;
        background-color: var(--surface-light-00);
        border-radius: 50%;
      }
    }
  }
}
</style>
